import { getTranslate } from 'react-localize-redux'
import { pickBy, keys } from 'lodash-es'
import moment from 'moment-timezone'
import store from 'core/store/store'
import { ymdFormat } from 'core/utils/constants'
import { ISelectOption, IOption } from '../components/fields/Fields.interfaces'
import { detect } from 'detect-browser'
import React, { ReactElement } from 'react'
import { ReactComponent as AmIcon } from '../../assets/images/manager-icons/amIcon.svg'
import { ReactComponent as CaIcon } from '../../assets/images/manager-icons/caIcon.svg'
import { ReactComponent as IotIcon } from '../../assets/images/manager-icons/iotIcon.svg'
import { ReactComponent as StIcon } from '../../assets/images/manager-icons/stIcon.svg'
import { ReactComponent as PkiIcon } from '../../assets/images/manager-icons/pkiIcon.svg'
import { ReactComponent as CtIcon } from '../../assets/images/manager-icons/ctIcon.svg'
import { ReactComponent as DamIcon } from '../../assets/images/manager-icons/damIcon.svg'
import { ReactComponent as OvIcon } from '../../assets/images/manager-icons/ovIcon.svg'
import { ReactComponent as IvIcon } from '../../assets/images/manager-icons/ivIcon.svg'
import { ReactComponent as DseIcon } from '../../assets/images/manager-icons/dseIcon.svg'
import { ReactComponent as GenericIcon } from '../../assets/images/manager-icons/genericIcon.svg'
import { ReactComponent as TlmIcon } from '../../assets/images/manager-icons/tlIcon.svg'
import { ReactComponent as KlIcon } from '../../assets/images/manager-icons/klIcon.svg'
import { ReactComponent as CcIcon } from '../../assets/images/manager-icons/ccIcon.svg'
import {ReactComponent as DnsTrustIcon} from '../../assets/images/manager-icons/dnsTrustIcon.svg'
import {ReactComponent as DnsPmIcon} from '../../assets/images/manager-icons/dnsPmIcon.svg'
import {ReactComponent as DevicetmIcon} from '../../assets/images/manager-icons/devicetmIcon.svg'
import { IPermission } from 'reducers/Reducers.interfaces'
import { ILocationStateFilter, ICountrySelectOption } from '../components/filters/Filters.interfaces'

export function generateRandomId(): string {
  return String(Math.round(Date.now() * Math.random()))
}

export function mapUserPermission(data: Record<string, boolean>): Record<string, string>[] {
  return keys(pickBy(data, p => p)).map(id => ({ id }))
}

export function mapUserPermissionString(data: Record<string, boolean>, allPermissions: IPermission[] | undefined): string[] | any {
  return Object.keys(data).map(permID => {
    if (keys(data).find((key) => key === permID && data[key])) {
      return allPermissions?.find(perm => perm.id === permID)?.name
    }
  }).filter(val => val !== undefined)
}

export function getEntityNames(entity: { name: string }[]): string {
  return entity.map(account => account.name).join(', ')
}

export function getEntityStatus(isActive: boolean): string {
  return isActive ? 'active' : 'inactive'
}

export function mapOptions(options: ISelectOption[]): IOption[] {
  return options.map(option => ({
    key: option.id,
    label: option.name
  }))
}

export function mapOptionsCommonUi(options?: ISelectOption[]): any[] {
  if (options) {
    return options.map(option => ({
      value: option.id,
      label: option.name
    }))
  }
  return []
}

export function formatDate(date: string, format?: string): string {
  return moment.utc(date).format(format ? format : 'DD-MMM-YYYY').replace('--', '-') as string
}

export function formatDateWithTimestampAndTimezone(date: string): string {
  const local = moment.utc(date).tz(moment.tz.guess());
  return local.format("DD-MMM-YYYY hh:mm A z");
}
export function formatTextFirstLetterCapitalize(str) {
  if (!str) return "";

  return str
    .split("_")              
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() 
        : word.toLowerCase()                                         
    )
    .join(" ");
}

export function formatRelativeTime(date: string): string {
  if (!date) return 'Never';
  const now = moment();
  const targetDate = moment(date);
  const diffHours = now.diff(targetDate, 'hours');
  const diffDays = now.diff(targetDate, 'days');

  if (diffHours < 1) {
    const diffMinutes = now.diff(targetDate, 'minutes');
    return `${diffMinutes} ${diffMinutes === 1 ? 'min' : 'mins'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hr' : 'hrs'} ago`;
  } else if (diffDays === 1) {
    return `Yesterday at ${targetDate.format('HH:mm')}`;
  } else {
    return targetDate.format('D MMM YYYY [at] HH:mm');
  }
}

export function mapCountryOptions(options: ICountrySelectOption[]): IOption[] {
  return options.filter(option => option.value !== 'CU').filter(option => option.value !== 'IR').filter(option => option.value !== 'SY').filter(option => option.value !== 'KP').map(option => ({
    key: option.value,
    label: option.label
  }))
}

export function mapCountryOptionsCommonUi(options: ICountrySelectOption[]): any[] {
  return options.filter(option => option.value !== 'CU').filter(option => option.value !== 'IR').filter(option => option.value !== 'SY').filter(option => option.value !== 'KP').map(option => ({
    value: option.value,
    label: option.label
  }))
}

export function isValidEmail(email: string): boolean {
  // eslint-disable-next-line no-useless-escape
  return Boolean(email.match(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/))
}

export function isValidPassword(password: string): boolean {
  return Boolean(password.match(/^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&+=]).+$/))
}

export const flatObject = (obj, concatenator = '.') => (
  Object.keys(obj).reduce(
    (acc, key) => {
      if (Object.prototype.toString.call(obj[key]) !== '[object Object]') {
        return {
          ...acc,
          [key]: obj[key]
        }
      }

      const flattenedChild = flatObject(obj[key], concatenator)

      return {
        ...acc,
        ...Object.keys(flattenedChild).reduce((childAcc, childKey) => ({
          ...childAcc,
          [`${key}${concatenator}${childKey}`]: flattenedChild[childKey]
        }), {})
      }
    },
    {}
  )
)

// this is function to get the translated string
export function translate(id: string) {
  return store ? getTranslate(store.getState().localize)(id) : id
}

export function getValueByKeyChain(dataSource: object, indexList: string[]): {} | string {
  return indexList.reduce((accumulator, currentValue) => accumulator[currentValue], dataSource)
}

export function filterByColumn(value, record, dataIndex, parseFunc?) {
  let source: any = dataIndex instanceof Array
    ? getValueByKeyChain(record, dataIndex)
    : record[dataIndex]

  if (source === undefined) return false

  if (source instanceof Array) {
    source = parseFunc ? parseFunc(source) : getEntityNames(source)
  } else if (parseFunc) {
    source = parseFunc(source)
  }

  return source
    .toString()
    .toLowerCase()
    .includes(value.toLowerCase())
}

export function parseStatusToText(status: string) {
  return translate(`common.statuses.${status}`)
}

export function parseDateToFormat(date: string, format = ymdFormat): string {
  return moment(date).format(format)
}

export function getFilterFromState(state?: { filter: ILocationStateFilter }) {
  if (state && state.filter) {
    const { columnId, value } = state.filter
    return {
      [columnId]: typeof value === 'string' ? value : value.key
    }
  }

  return undefined
}

export function downloadItem(response: any, extension: string = 'txt', itemName: string = 'Item'): void {
  /* eslint no-undef: off */
  const data = new Blob([response], { type: 'application/json' })
  const csvURL = window.URL.createObjectURL(data)
  const tempLink = document.createElement('a')
  tempLink.href = csvURL
  tempLink.setAttribute('download', `${itemName}-${moment().format(ymdFormat)}.${extension}`)
  tempLink.click()
}

const SUPPORTED_PLATFORMS = ['Mac', 'Windows']
export function isSupportedPlatform() {
  const browser = detect()
  return SUPPORTED_PLATFORMS.some(platform => {
    // OS can be like Android OS | Mac OS | Linux | Windows Mobile | Windows 3.11 | Windows 95...
    if (browser && browser.os) {
      return browser.os.includes('Mobile') ? false : browser.os.includes(platform)
    }
  })
}

export function isOauthFlow(state: string | null) {
  if (state != null) {
    if (getDecodedState(state).includes('oauthFlow=true')) {
      return true
    }
  }
  return false
}

export function showCreateOneLink(state: string | null) {
  if (state != null) {
    if (getDecodedState(state).includes('showCreateOneLink=true')) {
      return true
    }
  }
  return false
}

export function isSelfEnrollFlow(state: string | null) {
  if (state != null) {
    if (getDecodedState(state).includes('selfEnrollFlow=true')) {
      return true
    }
  }
  return false
}

export function isValidAccountIdPresent(state: string | null) {
  if (state != null) {
    if (atob(state).includes('accountId=')) {
      return true
    }
  }
  return false
}

export function getDecodedState(state: string) {
  return atob(state).split('&')
}

export const renderManagerIcon = (iconName, managerName): ReactElement | undefined => {
  switch (iconName) {
    case 'amIcon':
      return <AmIcon />;

    case 'caIcon':
      return <CaIcon />;

    case 'iotIcon':
      return <IotIcon />;

    case 'stIcon':
      return <StIcon />;

    case 'pkiIcon':
      return <PkiIcon />;

    case 'ctIcon':
      return <CtIcon />;

    case 'damIcon':
      return <DamIcon />;

    case 'ovIcon':
      return <OvIcon />;

    case 'ivIcon':
      return <IvIcon />;

    case 'dseIcon':
      return <DseIcon />;

    case 'tlIcon':
      return <TlmIcon />;

    case 'klIcon':
      return <KlIcon />;

    case 'ccIcon':
      return <CcIcon />;

    case 'dnsTrustIcon':
      return <DnsTrustIcon />;

    case 'dnsPmIcon':
      return <DnsPmIcon />;

    case 'devctmIcon':
      return <DevicetmIcon />;

    default:
      return <GenericIcon />;
  }
}

export const serviceIcon = (appName: string): ReactElement | undefined => {
  switch (appName) {
    case 'Account':
      return <AmIcon />;

    case 'CA Services':
      return <CaIcon />;

    case 'IoT Trust':
      return <IotIcon />;

    case 'Software Trust':
      return <StIcon />;

    case 'Content Trust':
      return <CtIcon />;

    case 'Organization Validation':
      return <OvIcon />;

    case 'Individual Validation':
      return <IvIcon />;

    case 'Document Signing Engine':
      return <DseIcon />;

    case 'Trust Lifecycle':
      return <TlmIcon />;

    case 'CertCentral':
      return <CcIcon />;

    case 'Device Trust':
      return <DevicetmIcon />;

    default:
      return <GenericIcon />;
  }
}

export const getManagerName = (id: any, keyLockerEnabled: boolean): string => {
  if (keyLockerEnabled && id === 'secure_software_manager') {
    return translate('dashboardLicense.applications.keylocker') as string
  } else {
    return translate('dashboardLicense.applications.' + id) as string
  }
}

export const getManagerPermissionGroupName = (id: any, keyLockerEnabled: boolean): string => {
  if (keyLockerEnabled && id === 'SECURE_SOFTWARE_MANAGER') {
    return translate('permissionGroups.KEYLOCKER') as string
  } else {
    return translate('permissionGroups.' + id) as string
  }
}

export function getValueFromState(searchString: string, stateDecoded: string) {
  const indexOfString = stateDecoded.indexOf(searchString)
  const ch = '&'
  let indexOfEndOfString = stateDecoded.indexOf(ch, indexOfString);
  if (indexOfString != -1 && indexOfEndOfString === -1) {
    indexOfEndOfString = stateDecoded.length
  }
  const value = indexOfString != -1 && indexOfEndOfString != -1 ? stateDecoded.substring(indexOfString, indexOfEndOfString) : ""
  if (value !== "") {
    return value.split('=')[1]
  }
  return ""
}

export const getOSName = (): string => {
  const browser = detect()
  return browser?.os || ''
}

export const lastModified = (date: string) => {
  const modifiedDate = moment(date).format('DD-MMM-YYYY')
  const modifiedToday = moment(date).isSame(Date.now(), 'day')
  const today = moment(Date.now()).format('DD-MMM-YYYY')
  if (modifiedDate && modifiedToday) {
    return translate(`common.today`) as string
  }
  if (!modifiedToday && moment(date).add(1, 'day').format('DD-MMM-YYYY') === today) {
    return translate(`common.yesterday`) as string
  }
  else {
    return formatDate(date)
  }
}
